(() => {
  'use strict';

  const productItemSelector = '[data-trending-product-item]';
  const mobileProductMedia = window.matchMedia('(max-width: 768px)');
  const trendFavoriteApiBase = `${window.location.protocol === 'https:' ? 'https' : 'http'}://informationcollection.benyuan1.com/api/trendFavorite`;

  function getStatusRegion(scope) {
    let region = scope.querySelector('[data-trending-status]');

    if (!region) {
      region = document.createElement('div');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('role', 'status');
      region.dataset.trendingStatus = '';
      Object.assign(region.style, {
        height: '1px',
        margin: '-1px',
        overflow: 'hidden',
        position: 'absolute',
        width: '1px',
        clip: 'rect(0 0 0 0)',
      });
      scope.append(region);
    }

    return region;
  }

  function announceError(button, message) {
    const scope = button.closest('[data-trending-product-list]') || document.body;
    const region = getStatusRegion(scope);
    region.textContent = message;
  }

  function getCartSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  async function addToCart(button) {
    const variantId = Number(button.dataset.variantId);

    if (!Number.isFinite(variantId) || variantId <= 0) {
      announceError(button, 'This product is unavailable.');
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    const originalLabel = button.dataset.addLabel || button.textContent.trim();
    button.dataset.addLabel = originalLabel;
    announceError(button, 'Adding product to your cart.');

    try {
      const sections = getCartSections();
      const config = FoxTheme.utils.fetchConfig('javascript');
      const response = await fetch(FoxTheme.routes.cart_add_url, {
        ...config,
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || 'Unable to add product to cart.');

      const cartResponse = await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() });
      const cart = await cartResponse.json();
      cart.sections = result.sections || {};
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });

      button.textContent = 'Added!';
      announceError(button, 'Product added to your cart.');
      document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: result } }));
      document.querySelector('cart-drawer')?.show(button);
    } catch (error) {
      announceError(button, error.message || 'Unable to add product to cart.');
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
      window.setTimeout(() => {
        button.textContent = originalLabel;
      }, 1200);
    }
  }

  function setProductVisibility(button, expanded) {
    const productList = button.closest('[data-trending-product-list]');
    if (!productList) return;

    const initialCount = Number(productList.dataset.mobileInitialCount) || 0;

    if (!mobileProductMedia.matches) {
      productList.querySelectorAll(productItemSelector).forEach((item) => {
        item.hidden = false;
      });
      button.hidden = true;
      return;
    }

    productList.querySelectorAll(productItemSelector).forEach((item, index) => {
      if (index >= initialCount) item.hidden = !expanded;
    });
    button.hidden = false;
    button.setAttribute('aria-expanded', String(expanded));
  }

  function toggleProducts(button) {
    setProductVisibility(button, button.getAttribute('aria-expanded') !== 'true');
  }

  function toggleProductList(button) {
    if (mobileProductMedia.matches) return;

    const productList = button.closest('[data-trending-product-list]');
    if (!productList) return;

    const products = productList.querySelector('.trending-article-template__products');
    if (!products) return;

    const collapsed = button.getAttribute('aria-expanded') === 'true';
    productList.classList.toggle('is-collapsed', collapsed);
    products.hidden = collapsed;
    button.setAttribute('aria-expanded', String(!collapsed));
    button.setAttribute('aria-label', `${collapsed ? 'Expand' : 'Collapse'} product list`);

    const expandButton = productList.querySelector('[data-trending-expand]');
    if (expandButton && !collapsed) {
      setProductVisibility(expandButton, expandButton.getAttribute('aria-expanded') === 'true');
    }
  }

  function toggleVideo(button) {
    const video = button.closest('.trending-article-template__video');
    const content = video?.querySelector('.trending-article-template__video-content');
    if (!video || !content) return;

    const collapsed = button.getAttribute('aria-expanded') === 'true';
    video.classList.toggle('is-collapsed', collapsed);
    content.hidden = collapsed;
    button.setAttribute('aria-expanded', String(!collapsed));
    button.setAttribute('aria-label', collapsed ? 'Expand video tutorial' : 'Collapse video tutorial');
  }

  function toggleStepGuide(button) {
    const guide = button.closest('.trending-article-template__step-guide');
    const content = guide?.querySelector('.trending-article-template__step-guide-content');
    if (!guide || !content) return;

    const collapsed = button.getAttribute('aria-expanded') === 'true';
    guide.classList.toggle('is-collapsed', collapsed);
    content.hidden = collapsed;
    button.setAttribute('aria-expanded', String(!collapsed));
    button.setAttribute('aria-label', collapsed ? 'Expand step guide' : 'Collapse step guide');
  }

  function getTrendFavoriteRoot() {
    return document.querySelector('[data-trending-article-favorite]');
  }

  function setTrendSaveState(button, isSaved) {
    button.setAttribute('aria-pressed', String(isSaved));
    if (!button.dataset.favoriteCount) {
      button.setAttribute('aria-label', isSaved
        ? button.dataset.savedLabel || 'Saved'
        : button.dataset.saveLabel || 'Save');
    }
  }

  async function postTrendFavorite(path, payload) {
    const response = await fetch(`${trendFavoriteApiBase}/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const result = await response.json().catch(() => null);
    const apiResult = result?.data && typeof result.data === 'object' && 'success' in result.data
      ? result.data
      : result;
    const isSuccess = apiResult?.success === true || result?.status === 'success';
    if (!response.ok || !isSuccess) {
      throw new Error(apiResult?.message || result?.message || `Trend favorite request failed (${response.status})`);
    }
    return apiResult;
  }

  async function initializeTrendSave() {
    const root = getTrendFavoriteRoot();
    const button = root?.querySelector('[data-trending-save]');
    const customerId = root?.dataset.customerId?.trim();
    if (!root || !button || !customerId) return;

    // Server state is authoritative; start every page render as not saved.
    setTrendSaveState(button, false);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const result = await postTrendFavorite('list', {
        user_id: customerId,
        category: 'all',
        type: 1,
      });
      const isSaved = (Array.isArray(result?.data) ? result.data : [])
        .some((item) => String(item?.trend_id) === button.dataset.trendId);
      setTrendSaveState(button, isSaved);
    } catch (error) {
      console.warn('Unable to load trend favorite state.', error);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async function toggleTrendSave(button) {
    const root = getTrendFavoriteRoot();
    const customerId = root?.dataset.customerId?.trim();
    const customerName = root?.dataset.customerName?.trim();
    const customerEmail = root?.dataset.customerEmail?.trim();
    if (!root || root.dataset.customerLoggedIn !== 'true' || !customerId) {
      root?.__likeShareAuth?.setTab?.('login');
      root?.__likeShareAuth?.openModal?.();
      return;
    }

    const trendId = button.dataset.trendId;
    const category = button.dataset.trendCategory;
    if (!trendId || !category || button.disabled) return;

    const isSaved = button.getAttribute('aria-pressed') === 'true';
    const countDelta = isSaved ? -1 : 1;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    setTrendSaveState(button, !isSaved);
    window.TrendFavoriteCounts?.adjust(trendId, countDelta);
    try {
      await postTrendFavorite(isSaved ? 'unfavorite' : 'favorite', {
        user_id: customerId,
        user_name: customerName,
        email: customerEmail,
        trend_id: trendId,
        ...(isSaved ? {} : { category, title: button.dataset.trendTitle }),
        type: 1,
      });
      window.TrendFavoriteCounts?.refreshTrend(trendId);
    } catch (error) {
      setTrendSaveState(button, isSaved);
      window.TrendFavoriteCounts?.adjust(trendId, -countDelta);
      console.warn('Unable to update trend favorite.', error);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('button');
    if (!button) return;

    if (button.matches('[data-trending-add]')) {
      addToCart(button);
    } else if (button.matches('[data-trending-product-collapse]')) {
      toggleProductList(button);
    } else if (button.matches('[data-trending-video-collapse]')) {
      toggleVideo(button);
    } else if (button.matches('[data-trending-step-collapse]')) {
      toggleStepGuide(button);
    } else if (button.matches('[data-trending-expand]')) {
      toggleProducts(button);
    } else if (button.matches('[data-trending-save]')) {
      toggleTrendSave(button);
    }
  });

  function syncProductVisibility() {
    document.querySelectorAll('[data-trending-expand]').forEach((button) => {
      setProductVisibility(button, button.getAttribute('aria-expanded') === 'true');
    });
  }

  syncProductVisibility();
  initializeTrendSave();
  window.TrendFavoriteCounts?.refresh();
  mobileProductMedia.addEventListener('change', syncProductVisibility);
})();
